/**
 * Pure classifier mapping WebSocket close codes to recovery actions.
 *
 * Both `game-connection-manager` and `websocket-service` (lobby) read this
 * table — one source of truth for "what does code N mean?". Side effects
 * (state mutations, redirects, toasts) stay bespoke per manager. The
 * classifier only answers: should we retry, treat this as terminal, or kick
 * off auth recovery?
 *
 * Per Inv 6, no `kind` corresponds to "call signOut()". Auth-related codes
 * map to `auth-recovery`, which emits `session-expired` for the overlay to
 * handle.
 */

import { WsCloseCodeEnum } from '@danda/shared';

export type CloseAction =
  /** Transient — backoff and reconnect. */
  | { kind: 'retry'; backoff: 'exponential' }
  /**
   * Session is over but we want to keep `lastSessionId` so a follow-up
   * `startSession()` in the same tab is recognised as a switch (e.g. the
   * recap UI relies on this).
   */
  | { kind: 'terminal-keep-id' }
  /**
   * Session is over and the manager should null `lastUrl` AND
   * `lastSessionId`. Used when the player has no business reconnecting to
   * this session (kicked, not-a-participant, dead-on-arrival).
   */
  | { kind: 'terminal-clear-all' }
  /** Auth failure — emit `session-expired`, do NOT call signOut. */
  | { kind: 'auth-recovery' }
  /** Player removed from the game by another player or the server. */
  | { kind: 'kicked' }
  /** PM token balance below threshold — bounce to /buy-tokens flow. */
  | { kind: 'insufficient-tokens' };

const TABLE: Readonly<Record<number, CloseAction>> = {
  [WsCloseCodeEnum.NORMAL_CLOSURE]: { kind: 'retry', backoff: 'exponential' },
  [WsCloseCodeEnum.GOING_AWAY]: { kind: 'retry', backoff: 'exponential' },

  [WsCloseCodeEnum.NOT_PARTICIPANT]: { kind: 'terminal-clear-all' },
  [WsCloseCodeEnum.SESSION_NOT_FOUND]: { kind: 'terminal-clear-all' },

  [WsCloseCodeEnum.GAME_ENDED]: { kind: 'terminal-keep-id' },
  [WsCloseCodeEnum.PLAYER_KICKED]: { kind: 'kicked' },

  [WsCloseCodeEnum.UNAUTHORIZED]: { kind: 'auth-recovery' },
  [WsCloseCodeEnum.FORBIDDEN]: { kind: 'auth-recovery' },
  // Backend doesn't emit TOKEN_EXPIRED any more (verified empirically), but if
  // an old build does, route through auth recovery rather than a special case.
  [WsCloseCodeEnum.TOKEN_EXPIRED]: { kind: 'auth-recovery' },

  [WsCloseCodeEnum.INSUFFICIENT_TOKENS]: { kind: 'insufficient-tokens' },

  [WsCloseCodeEnum.SIGNED_OUT_ELSEWHERE]: { kind: 'kicked' },
};

const DEFAULT_ACTION: CloseAction = { kind: 'retry', backoff: 'exponential' };

/**
 * Classify a WebSocket close code into a recovery action. Unknown codes
 * default to `retry` so a future server-side variant we haven't taught the
 * client about doesn't dead-end the connection.
 */
export function classifyCloseCode(code: number): CloseAction {
  return TABLE[code] ?? DEFAULT_ACTION;
}
